import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { FileUpload } from "./FileUpload";

const Section = ({ title, children }) => (
  <div className="bg-white border border-zinc-200 p-6">
    <h2 className="font-heading font-black text-lg border-l-4 border-red-600 pl-3 mb-5">{title}</h2>
    <div className="grid sm:grid-cols-2 gap-4">{children}</div>
  </div>
);

const F = ({ label, name, form, set, type, full, hint }) => (
  <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}>
    <Label>{label}</Label>
    {type === "textarea" ? <Textarea data-testid={`settings-${name}`} rows={3} value={form[name] || ""} onChange={(e) => set(name, e.target.value)} className="rounded-none" />
      : <Input data-testid={`settings-${name}`} value={form[name] || ""} onChange={(e) => set(name, e.target.value)} className="rounded-none" />}
    {hint && <p className="text-xs text-zinc-500">{hint}</p>}
  </div>
);

export default function SettingsManager() {
  const { refresh } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setForm(r.data)); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.put("/settings", form); await refresh(); toast.success("Paramètres enregistrés"); } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  if (!form) return <div className="text-zinc-500">Chargement…</div>;

  return (
    <form onSubmit={save} className="space-y-6" data-testid="settings-manager">
      <div className="flex items-start justify-between gap-4">
        <div><h1 className="font-heading text-3xl font-black">Paramètres du site</h1><p className="text-zinc-500 text-sm mt-1">Logo, bannière publicitaire, coordonnées et réseaux sociaux.</p></div>
        <Button data-testid="settings-save-btn" disabled={saving} className="bg-red-600 hover:bg-red-700 rounded-none font-bold uppercase text-xs tracking-wider">{saving ? "Enregistrement…" : "Enregistrer"}</Button>
      </div>
      <Section title="Identité">
        <F label="Nom du site" name="site_name" form={form} set={set} />
        <F label="Slogan" name="tagline" form={form} set={set} />
        <div className="space-y-1.5"><Label>Logo (PNG/SVG fond transparent conseillé, hauteur ~64px)</Label><FileUpload value={form.logo_url} onChange={(v) => set("logo_url", v)} accept="image/*" label="Téléverser le logo" testId="settings-logo" /></div>
        <div className="space-y-1.5"><Label>Favicon (icône d'onglet)</Label><FileUpload value={form.favicon_url} onChange={(v) => set("favicon_url", v)} accept="image/*" label="Téléverser le favicon" testId="settings-favicon" /></div>
      </Section>
      <Section title="Bannière publicitaire (en-tête, 540×80)">
        <div className="space-y-1.5 sm:col-span-2"><Label>Image de la pub</Label><FileUpload value={form.ad_image_url} onChange={(v) => set("ad_image_url", v)} accept="image/*" label="Téléverser l'image de la publicité" testId="settings-ad" /></div>
        <F label="Lien au clic (https://…)" name="ad_link" form={form} set={set} />
        <F label="Titre (si aucune image)" name="ad_title" form={form} set={set} />
        <F label="Sous-titre (si aucune image)" name="ad_subtitle" form={form} set={set} full />
      </Section>
      <Section title="Pied de page & contact">
        <F label="Texte de présentation" name="footer_about" form={form} set={set} type="textarea" full />
        <F label="Adresse" name="address" form={form} set={set} />
        <F label="E-mail de contact" name="contact_email" form={form} set={set} />
        <F label="Numéro WhatsApp (ex. 243812345678)" name="whatsapp_number" form={form} set={set} hint="Affiche une icône WhatsApp dans la barre du haut." />
      </Section>
      <Section title="Réseaux sociaux">
        <F label="Facebook" name="facebook_url" form={form} set={set} />
        <F label="X / Twitter" name="twitter_url" form={form} set={set} />
        <F label="YouTube" name="youtube_url" form={form} set={set} />
        <F label="Telegram" name="telegram_url" form={form} set={set} />
      </Section>
    </form>
  );
}
